import React, { useCallback, useMemo } from 'react';
import { FormDrawer, Badge } from '../../../components/ui';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import useLicenseForm from '../hooks/useLicenseForm.js';
import useLicenseMetadata from '../hooks/useLicenseMetadata.js';
import LicenseForm from './LicenseForm.jsx';

const CREATE_SUBTITLE =
  'Define a global license plan. Select a standard permission profile for RBAC and set user and AI credit entitlements.';

const EDIT_SUBTITLE =
  'Update plan details, billing, permissions, and entitlements for this license.';

const DRAWER_WIDTH = { xs: '100%', sm: 480, md: 640, lg: 720 };

export default function LicenseFormDrawer({ open, license = null, onClose, onSuccess }) {
  const { showSnackBar } = useSnackBar();
  const formState = useLicenseForm({ license, onClose, onSuccess, open });
  const metadata = useLicenseMetadata({ enabled: open });
  const { isEditMode, isSubmitting, isDirty, validationAttempt, submit, handleClose, form, errors, updateField } =
    formState;
  const isBusy = isSubmitting || metadata.isLoading;

  const editBadge = useMemo(() => {
    if (!isEditMode) {
      return null;
    }
    const planLabel = license?.alias || license?.name || 'License plan';
    return (
      <Badge
        variant="outline"
        size="sm"
        sx={{ fontWeight: 500 }}
      >
        {`Editing ${planLabel}`}
      </Badge>
    );
  }, [isEditMode, license?.alias, license?.name]);

  const onDrawerClose = useCallback(() => {
    if (!isSubmitting) {
      handleClose();
    }
  }, [handleClose, isSubmitting]);

  const onDrawerSubmit = useCallback(async () => {
    const result = await submit();
    if (!result.success && result.message) {
      showSnackBar(result.message, 'error');
    }
  }, [showSnackBar, submit]);

  return (
    <FormDrawer
      open={open}
      onClose={onDrawerClose}
      title={isEditMode ? 'Edit License' : 'Create License'}
      subtitle={isEditMode ? EDIT_SUBTITLE : CREATE_SUBTITLE}
      headerBadge={editBadge}
      submitLabel={
        isSubmitting
          ? isEditMode
            ? 'Saving...'
            : 'Creating...'
          : isEditMode
            ? 'Save Changes'
            : 'Create License'
      }
      onSubmit={onDrawerSubmit}
      isSubmitting={isSubmitting}
      submitDisabled={metadata.isLoading}
      disableClose={isBusy}
      confirmBeforeClose={isDirty}
      width={DRAWER_WIDTH}
    >
      <LicenseForm
        form={form}
        errors={errors}
        updateField={updateField}
        license={license}
        features={metadata.features}
        metadataLoading={metadata.isLoading}
        metadataError={metadata.error}
        validationAttempt={validationAttempt}
      />
    </FormDrawer>
  );
}
